import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { useValidate, type ValidateResponseType } from "@/hooks/useValidation";
import { Iris, type IrisColor } from "@/components/Iris/Iris";
import { AuthStatusIndicator } from "@/components/AuthStatusIndicator/AuthStatusIndicator";
// import { Button } from "@/components/Button/Button";
import { Header } from "@/components/Header/Header";
import { Instructions } from "@/components/Instructions/Instructions";
import { useAppState } from "@/contexts/AppStateContext";
import { useStyle } from "@/contexts/StyleContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useDebouncedValue } from "@mantine/hooks";
import { useBenchmark } from "@/contexts/BenchmarkContext";
import { QRScanner as ClassicScanner } from "@/components/QRScanner/QRScanner";
import { DynamQRScanner } from "@/components/QRScanner/QRScanner.foundational";
import { type ProgressBarHandle, ProgressBorder } from "./ProgressBorder/ProgressBorder";
import { GetStartedTemplate } from "./Template/GetStartedTemplate";

const USE_DYNAMSOFT_SCANNER = import.meta.env.VITE_USE_DYNAMSOFT_SCANNER === 'true';
// Utility function to delay execution of a function
// const delay = (fn: () => void, ms: number) => {
//     return setTimeout(fn, ms);
// };

export const QRScannerClient = () => {
    const { captureScanResult } = useBenchmark();
    const { appState, setAppState } = useAppState(); // appState now has an explicit type
    const [codes, setCodes] = useState<string[]>([]);
    const [irisColor, setIrisColor] = useState<IrisColor>('primary');
    const [responseMessage, setResponseMessage] = useState<string>();
    const [transitionMessage, setTransitionMessage] = useState<string>();
    const { validate } = useValidate();
    const [videoStream, setVideoStream] = useState(false);
    const { setBrandingColors } = useStyle();
    const { setTheme } = useTheme();
    const pendingValidationRef = useRef<{ codes: string[] } | null>(null);

    const progressBarRef = useRef<ProgressBarHandle>(null);
    const getCodeFromUrl = () => {
        if (typeof window === "undefined") return;
        const navEntry = window.performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;
        const navType = navEntry?.type;
        if (navType !== 'navigate') return;
        try {
            const href = decodeURIComponent(window.location.href || "");
            // Remove only the protocol (http:// or https://)
            const normalizedHref = href.replace(/^https?:\/\//, "");
            // Remove trailing slash 
            const removeTrailingSlash = normalizedHref.replace(/\/$/, "");

            // Only setCodes for h0.to/N pattern (h0.to followed by / and at least one character)
            if (removeTrailingSlash.startsWith('h0.to/')) {
                const path = removeTrailingSlash.slice('h0.to/'.length);
                if (path.length > 0) {
                    setCodes((prev) =>
                        prev.includes(removeTrailingSlash) ? prev : [...prev, removeTrailingSlash]
                    );
                }
            }
            // For all other URLs (including just h0.to, h0.to/, and other domains), do nothing
        } catch (err) {
            console.error("[QRScannerClient] error parsing window.location.href", err);
        }
    }

    const handleCodeDetected = useCallback((code: string) => {
        if (appState === 'playing' && !codes.includes(code)) {
            setCodes((prevCodes) => {
                const updatedCodes = [...prevCodes, code];
                console.log(`[QRScannerClient] Code #${updatedCodes.length} detected:`, code);
                console.log(`[QRScannerClient] All codes so far:`, updatedCodes);
                return updatedCodes;
            });
        }
    }, [appState, codes]);

    // No-op capture handler - not used in 5-code flow
    const handleCapture = useCallback(() => {}, []);

    const [debouncedProgress] = useDebouncedValue(
        Math.min((codes.length / 3) * 100, 100), 100
    );

    useEffect(() => {
        progressBarRef.current?.setProgress(debouncedProgress);
    }, [debouncedProgress]);

    /*
        Validate the codes and serial code.
            - Move the app state to 'validating'
            - If the codes are valid, set the success message and redirect URL
            - If the codes are not valid, set the error message and show the contact button
            - Move the app state to 'done'
    */
    const handleValidate = useCallback(async (codes: string[]) => {
        setAppState('validating');
        const response = await validate(codes) as ValidateResponseType;
        if (response.valid) {
            setTimeout(() => {
                setResponseMessage(response.label);
            }, 2000);
        } else {
            setIrisColor('error');
            setResponseMessage(response.message);
        }
        setAppState('done');
    }, [setAppState, validate]);


    useEffect(() => {
        if (codes.length >= 3) {
            pendingValidationRef.current = { codes };
        }
    }, [codes]);



    const handleAnimateEnd = useCallback(() => {
        if (pendingValidationRef.current) {
            const { codes: codesRef } = pendingValidationRef.current;
            console.log("[QRScannerClient] Triggering validation with final codes:", codesRef);
            console.log(`[QRScannerClient] Total codes to validate: ${codesRef.length}`);
            handleValidate(codesRef)
        }

    }, [handleValidate]);

    /*
        State Machine Effects:
            * Separate out each app state logic into its own side effects.
            * There are two types of "effects" per state. 
                - INTO - when we transition from one state to another 
                - INSIDE - when we are currently in that state and something changes that requires action.
            * We separate and group our useEffects accordingly. For example, for the "playing" state,
              there is an INTO effect as well as several different INSIDE effects that play different roles.),
    */
    /*
         appState = initializing
             - INTO - Nothing Side Effects
             - INSIDE 
                 - Once the video stream is active, we move to instructions state
     */
    useEffect(() => {
        if (appState !== 'initializing') return;

        if (videoStream) {
            setAppState('instructions');
        }
    }, [appState, setAppState, videoStream]);

    /*
        appState = instructions || waitingforuid - no state-specific side effects needed
        appState = playing
            - INTO 
                - get the first code from the current url if present
                - set the transition message to "Place the code in the frame"
    */
    useEffect(() => {
        if (appState !== 'playing') return;
        getCodeFromUrl();
        setTransitionMessage("Place the code in the frame");
    }, [appState]);

    /*
        INSIDE 
            - Update transition messages based on codes captured
            - When first code is scanned, set default branding colors and logo
    */
    useEffect(() => {
        if (appState !== 'playing') return;
        // When first code is scanned, set default theme and load sample logo
        if (codes.length === 1) {
            setTransitionMessage("Great!");
            // Set default colors and logo in theme
        }
        else if (codes.length === 2) {
            setTransitionMessage("Great!");
            setTimeout(() => {
                setTransitionMessage("Keep still, scanning codes");
            }, 1500);
        }
        // When all 3 are present, clear the transition message
        else if (codes.length === 3) {
            setTransitionMessage("");
        }
    }, [codes, appState, setBrandingColors, setTheme]);


    /*
        appState = validating - no state-specific side effects needed
        appState = showtemplate
            INTO
                - If branding has changed, update the theme
    */
    // useEffect(() => {
    //     if (appState === 'showtemplate' && branding) {
    //         setTheme(branding as ITheme);
    //     }
    // }, [appState, branding, setTheme]);


    /*
        Other Misc Effects
            - Benchmarking: Capture the scan results whenever they change
    */
    useEffect(() => {
        captureScanResult({
            qrOne: codes.length >= 1 ? codes[0] : undefined,
            qrTwo: codes.length >= 2 ? codes[1] : undefined,
        });
    }, [codes, captureScanResult]);

    return (
        <div className={"flex flex-col items-center justify-start min-h-screen text-white font-['FoundersGroteskMonoRegular']"}>
            <Header />
            <div className="flex flex-grow flex-col" data-h010-scanner-container>
                {appState === 'instructions' && <Instructions />}
                {appState === 'showtemplate' && (
                    <div className="flex flex-col items-center justify-center h-full">
                        <div className="flex flex-col items-center w-full max-w-md px-4">
                            <GetStartedTemplate />
                        </div>
                    </div>
                )}
                {appState === 'initializing' && <InitializingComponent />}
                {appState !== 'showtemplate' && (
                    <IrisComponent
                        onCodeDetected={handleCodeDetected}
                        onCapture={handleCapture}
                        onPlaying={() => setVideoStream(true)}
                        ref={progressBarRef}
                        color={irisColor}
                        responseMessage={responseMessage}
                        onAnimateEnd={handleAnimateEnd}
                        progress={0}
                    />
                )}
                {appState === 'playing' && transitionMessage && (
                    <div className="mt-4">
                        <h2
                            key={transitionMessage}
                            className={`text-center text-color text-xl font-bold ${transitionMessage === "Great!" ? "rise-up" : "rise-up-stay"}`}
                        >
                            {transitionMessage}
                        </h2>
                    </div>
                )}
            </div>
            {appState === 'instructions' && (
                <div className="w-full">
                    <footer className="h-[10vh] w-full">
                        <p className="text-center text-md text-gray-400 ">
                            Powered securely by{" "}
                            <a
                                href="https://h010.com"
                                className="font-bold text-white cursor-pointer font-['FoundersGroteskBold']"
                            >
                                H010
                            </a>
                        </p>
                    </footer>
                </div>
            )}
        </div>
    );
}

interface IrisComponentProps {
    onCodeDetected: (code: string) => void;
    onCapture: (image: string) => void;
    onPlaying: () => void;
    progress: number;
    color: IrisColor;
    responseMessage?: string; // Added responseMessage prop
    onAnimateEnd?: () => void;
}
/*
  IrisComponent AppState Beahviours:
    - The component needs to remain mounted from the start to trigger camera positions. Knowing that:
    - initializing: Hidden
    - tutorial: Hidden
    - playing: Visible + Open
    - validating: Visible + Shrink
    - redirecting: The camera should be stopped and invisible.
*/

const InitializingComponent = () => {
    return (
        <div className="flex flex-col flex-1 gap-20">
            <div className="flex-col mb-0 pt-12">
                <div className="flex flex-col space-y-12">
                    <h2 className="flex text-xl">
                        Camera access required
                        to scan H010
                    </h2>
                </div>
            </div>
        </div>
    )
}

const IrisComponent = forwardRef<ProgressBarHandle, IrisComponentProps>(({ onCodeDetected, onCapture, onPlaying, progress, color, responseMessage, onAnimateEnd }, ref) => {
    const { appState } = useAppState();
    const isIrisHidden = appState === 'initializing' || appState === 'instructions' || appState === 'showtemplate';
    const irisStatus = ['waitingforuid', 'playing', 'validating'].includes(appState) ? 'open' : 'shrink';
    const isPlaying = ['waitingforuid', 'playing', 'validating'].includes(appState);
    const bgColor = ['done'].includes(appState);
    // const progressBarRef = useRef<ProgressBarHandle>(null);

    // useEffect(() => {
    //     progressBarRef.current?.setProgress(progress);
    // }, [progress]);
    return (
        <div className="relative">

            <Iris
                hidden={isIrisHidden}
                status={irisStatus}
                color={color}
                appState={bgColor}
            >
                {
                    USE_DYNAMSOFT_SCANNER ? (
                        <DynamQRScanner
                            onCodeDetected={onCodeDetected}
                            onCapture={onCapture}
                            onPlaying={onPlaying}
                        />
                    ) : (
                        <ClassicScanner
                            onCodeDetected={onCodeDetected}
                            onCapture={onCapture}
                            onPlaying={onPlaying}
                        />
                    )
                }
                {isPlaying &&
                    <ProgressBorder
                        ref={ref}
                        initialProgress={progress}
                        irisStatus={irisStatus}
                        onAnimateEnd={onAnimateEnd}
                    />}
                {/* Choose between Dynamsoft/Zxing */}
                {irisStatus === 'shrink' && <AuthStatusIndicator status={color === 'error' ? 'error' : 'success'} />}
            </Iris>
            {appState !== 'showtemplate' && appState !== 'playing' && responseMessage && (
                <div className="flex flex-col items-center justify-center px-4 text-center -mt-12 w-full max-w-md">
                    {color === 'error' ? (
                        <p className="text-md md:text-base">
                            {responseMessage}
                        </p>
                    ) : (
                        <>
                            <p className="text-xl md:text-xl text-white ">
                                Digi H010 is a patented device to device authentication tool
                                that enables <br /> highly secure e-ticket verification.
                            </p>
                            <p className="text-md md:text-base text-gray-400 mt-12">
                                © 2026. All rights reserved.
                            </p>
                            <p className="text-md md:text-base text-gray-400 mt-2">
                                Contact <a href="mailto:hello@h010.com" className="text-white hover:underline">hello@h010.com</a>
                            </p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
});

IrisComponent.displayName = 'IrisComponent';